import { showAlert } from "../components/common/ErrorAlert/ErrorAlert";
import api from "./api";

const formatErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Something went wrong";

  if (Array.isArray(data.details) && data.details.length > 0) {
    const detailMessages = data.details
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.product) {
          return `${item.product} doesn't have enough stock`;
        }
        if (item?.message) return item.message;
        if (item?.error) return item.error;
        return null;
      })
      .filter(Boolean);

    if (detailMessages.length > 0) {
      return detailMessages.join(", ");
    }
  }

  if (typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }

  return "Something went wrong";
};

export async function getTransferDetails(uuid) {
  try {
    const response = await api.get(`/stock/transfer/${uuid}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getTransfersList(params = {}) {
  try {
    const response = await api.get("/stock/transfer/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function CreateTransfer(payload) {
  try {
    const response = await api.post("stock/transfer/create/", payload);
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
  }
}

export async function CreateTransitTransfer(transferId) {
  try {
    const response = await api.post(
      `stock/transfer/transit/?transfer_id=${transferId}`,
    );
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
    throw error; // rethrow if you want to handle in component
  }
}

export async function CompleteTransfer(transferId) {
  try {
    const response = await api.post(
      `stock/transfer/complete/?transfer_id=${transferId}`,
    );
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
    throw error; // rethrow if you want to handle in component
  }
}

export async function CancelTransfer(uuid) {
  try {
    const response = await api.post(`stock/transfer/${uuid}/cancel/`);
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
    throw error;
  }
}

export async function DeleteTransfer(id) {
  try {
    const response = await api.delete(`stock/transfer/delete/?id=${Array.isArray(id) ? id.join(',') : id}`);
    return response;
  } catch (e) {
    throw e;
  }
}

export async function ArchiveTransfers(id, id_list) {
  try {
    let response;
    if (id_list) {
      response = await api.post("stock/transfer/archive/", {
        id_list: id_list,
      });
    } else {
      response = await api.get(`stock/transfer/archive/?id=${id}`);
    }
    return response;
  } catch (error) {
    throw error;
  }
}

export async function UpdateTransfer(id, payload) {
  try {
    const response = await api.post(`stock/transfer/update/?id=${id}`, payload);
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
  }
}

export async function duplicateTransfer(id) {
  const response = await api.post(`stock/transfer/duplicate/?id=${id}`);
  return response.data;
}

export async function duplicateInventoryAdjustment(id) {
  const response = await api.post(
    `stock/inventory-adjustment/duplicate/?id=${id}`,
  );
  return response.data;
}

export async function getInventoryAdjustmentDetails(uuid) {
  try {
    const response = await api.get(`/stock/inventory-adjustment/${uuid}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getInventoryAdjustmentsList(params = {}) {
  try {
    const response = await api.get("/stock/inventory-adjustment/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function CreateInventoryAdjustment(payload) {
  try {
    const response = await api.post(
      "stock/inventory-adjustment/create/",
      payload,
    );
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
  }
}

export async function CompleteInventoryAdjustment(adjustmentId) {
  try {
    const response = await api.post(
      `stock/inventory-adjustment/complete/?adjustment_id=${adjustmentId}`,
    );
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
    throw error; // rethrow if you want to handle in component
  }
}

export async function UpdateInventoryAdjustment(id, payload) {
  try {
    const response = await api.post(
      `stock/inventory-adjustment/update/?id=${id}`,
      payload,
    );
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
  }
}

export async function CancelInventoryAdjustment(adjustmentId) {
  try {
    const response = await api.post(
      `stock/inventory-adjustment/cancel/?adjustment_id=${adjustmentId}`,
    );
    return response;
  } catch (error) {
    showAlert(formatErrorMessage(error));
    throw error;
  }
}

export async function DeleteInventoryAdjustment(id) {
  try {
    const response = await api.delete(
      `stock/inventory-adjustment/delete/?id=${Array.isArray(id) ? id.join(',') : id}`,
    );
    return response;
  } catch (e) {
    throw e;
  }
}

export async function ArchiveInventoryAdjustment(id, id_list) {
  try {
    let response;
    if (id_list) {
      response = await api.post("stock/inventory-adjustment/archive/", {
        id_list: id_list,
      });
    } else {
      response = await api.get(`stock/inventory-adjustment/archive/?id=${id}`);
    }
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getBatchTrackingDetails(uuid) {
  try {
    const response = await api.get(`/stock/batch-tracking/${uuid}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getBatchTrackingList(params = {}) {
  try {
    const response = await api.get("/stock/batch-tracking/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateBatchTracking(uuid, data) {
  try {
    const response = await api.put(`/stock/batch-tracking/${uuid}/update/`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function ScanStockLayer(barcode, warehouseId) {
  try {
    const response = await api.get(
      `/stock/scan-layer/?barcode=${encodeURIComponent(barcode)}&warehouse_id=${warehouseId}`
    );
    return response.data?.data || response.data;
  } catch (error) {
    throw error;
  }
}

